import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  ReactFlowInstance,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useAuth } from '@/hooks/useAuth';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Layers, Filter, Search, Users, FileText, Target, Sparkles, Brain } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { analysisSessionService, analysisClusterService, getNotesFromInterviews, AnalysisSession, AnalysisNote, AnalysisCluster } from '@/services/analysis';
import { NoteType, AnalysisResults } from '@/types/notes';
import { AnalysisStickyNote } from '@/components/AnalysisStickyNote';
import { ClusterNode } from '@/components/ClusterNode';
import { analyzeNotes } from '@/services/openai';
import { AnalysisPanel } from '@/components/AnalysisPanel';
import { MobileAnalysisPanel } from '@/components/mobile/MobileAnalysisPanel';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUsageTracking } from '@/hooks/useUsageTracking';

const noteTypes: { type: NoteType; label: string; color: string }[] = [
  { type: 'pain', label: 'Pain Points', color: 'bg-red-100 text-red-700' },
  { type: 'emotion', label: 'Emotions', color: 'bg-pink-100 text-pink-700' },
  { type: 'behaviour', label: 'Behaviours', color: 'bg-blue-100 text-blue-700' },
  { type: 'insight', label: 'Insights', color: 'bg-green-100 text-green-700' },
  { type: 'opportunity', label: 'Opportunities', color: 'bg-purple-100 text-purple-700' },
  { type: 'quote', label: 'Quotes', color: 'bg-yellow-100 text-yellow-700' },
  { type: 'note', label: 'Notes', color: 'bg-gray-100 text-gray-700' },
];

const nodeTypes = {
  analysisStickyNote: AnalysisStickyNote,
  cluster: ClusterNode,
};

const MultiAnalysis = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [reactFlowNodes, setReactFlowNodes, onNodesChange] = useNodesState([]);
  const [reactFlowEdges, setReactFlowEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  
  const [analysisSession, setAnalysisSession] = useState<AnalysisSession | null>(null);
  const [allNotes, setAllNotes] = useState<AnalysisNote[]>([]);
  const [clusters, setClusters] = useState<AnalysisCluster[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<AnalysisNote[]>([]);
  const [selectedNoteTypes, setSelectedNoteTypes] = useState<Set<NoteType>>(new Set(noteTypes.map(nt => nt.type)));
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateClusterOpen, setIsCreateClusterOpen] = useState(false);
  const [newClusterName, setNewClusterName] = useState('');
  const [newClusterDescription, setNewClusterDescription] = useState('');
  
  // AI Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(false);
  
  const isMobile = useIsMobile();
  const { checkCanRunAnalysis } = useUsageTracking();

  console.log('MultiAnalysis rendered with:', { analysisId, user: !!user, authLoading });

  const handleMoveNoteToCluster = useCallback(async (noteId: string, clusterId: string) => {
    // Implementation for moving note to cluster
    console.log('Move note to cluster:', { noteId, clusterId });
  }, []);

  const handleUpdateCluster = useCallback(async (clusterId: string, updates: Partial<AnalysisCluster>) => {
    try {
      const updatedCluster = await analysisClusterService.update(clusterId, updates);
      setClusters(prev => prev.map(c => c.id === clusterId ? updatedCluster : c));
    } catch (error) {
      console.error('Error updating cluster:', error);
    }
  }, []);

  const handleDeleteCluster = useCallback(async (clusterId: string) => {
    try {
      await analysisClusterService.delete(clusterId);
      setClusters(prev => prev.filter(c => c.id !== clusterId));
      toast({
        title: "Cluster Deleted",
        description: "The theme cluster has been deleted.",
      });
    } catch (error) {
      console.error('Error deleting cluster:', error);
      toast({
        title: "Error",
        description: "Failed to delete cluster.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleRemoveNoteFromCluster = useCallback(async (noteId: string, clusterId: string) => {
    // Implementation for removing note from cluster
    console.log('Remove note from cluster:', { noteId, clusterId });
  }, []);

  // Save positions periodically (not on every change to avoid lag)
  const savePositionsRef = useRef<NodeJS.Timeout>();
  
  const saveNodePositions = useCallback(async () => {
    if (!analysisSession || !reactFlowInstance) return;
    
    try {
      const nodes = reactFlowInstance.getNodes();
      const nodePositions: Record<string, { x: number; y: number }> = {};
      
      // Only save positions for note nodes (not clusters, they have their own persistence)
      nodes.forEach(node => {
        if (node.type === 'analysisStickyNote') {
          nodePositions[node.id] = node.position;
        }
      });
      
      const currentCanvasData = (analysisSession.canvas_data as any) || {};
      await analysisSessionService.update(analysisSession.id, {
        canvas_data: {
          ...currentCanvasData,
          nodePositions
        }
      });
      
      setAnalysisSession(prev => prev ? {
        ...prev,
        canvas_data: { ...currentCanvasData, nodePositions }
      } : null);
      
    } catch (error) {
      console.error('Error saving node positions:', error);
    }
  }, [analysisSession, reactFlowInstance]);

  // Debounced position saving
  const debouncedSavePositions = useCallback(() => {
    if (savePositionsRef.current) {
      clearTimeout(savePositionsRef.current);
    }
    savePositionsRef.current = setTimeout(saveNodePositions, 2000); // 2 second debounce
  }, [saveNodePositions]);

  // Handle node changes without interfering with React Flow
  const handleNodesChange = useCallback((changes: any[]) => {
    onNodesChange(changes);
    
    const hasPositionChanges = changes.some(change => 
      change.type === 'position' && change.dragging === false
    );
    
    if (hasPositionChanges) {
      debouncedSavePositions();
    }
  }, [onNodesChange, debouncedSavePositions]);

  // Load analysis session and notes
  useEffect(() => {
    const loadAnalysisData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load analysis session
        const session = await analysisSessionService.getById(analysisId);
        if (!session) {
          throw new Error('Analysis session not found');
        }
        setAnalysisSession(session);

        // Load notes from all selected interviews
        const notes = await getNotesFromInterviews(session.note_space_ids);
        setAllNotes(notes);

        // Load existing clusters
        const existingClusters = await analysisClusterService.getBySessionId(analysisId);
        setClusters(existingClusters);

        console.log('Loaded analysis data:', { session, notesCount: notes.length, clustersCount: existingClusters.length });
      } catch (err) {
        console.error('Error loading analysis data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    loadAnalysisData();
  }, [analysisId]);

  // Filter notes based on type and search
  useEffect(() => {
    let filtered = allNotes.filter(note => selectedNoteTypes.has(note.type));

    if (searchQuery.trim()) {
      filtered = filtered.filter(note =>
        note.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.source_interview_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredNotes(filtered);
  }, [allNotes, selectedNoteTypes, searchQuery]);

  // Convert notes to React Flow nodes
  useEffect(() => {
    const noteNodes: Node[] = filteredNotes.map((note, index) => {
      // Check if we have saved position data for this note
      const savedCanvasData = analysisSession?.canvas_data as any;
      const savedPosition = savedCanvasData?.nodePositions?.[note.id];
      
      return {
        id: note.id,
        type: 'analysisStickyNote',
        position: savedPosition || {
          x: 100 + (index % 5) * 280,
          y: 100 + Math.floor(index / 5) * 200
        },
        data: {
          ...note,
          onMoveToCluster: handleMoveNoteToCluster,
        }
      };
    });

    const clusterNodes: Node[] = clusters.map(cluster => ({
      id: cluster.id,
      type: 'cluster',
      position: { x: cluster.position_x, y: cluster.position_y },
      data: {
        ...cluster,
        onUpdateCluster: handleUpdateCluster,
        onDeleteCluster: handleDeleteCluster,
        onRemoveNoteFromCluster: handleRemoveNoteFromCluster,
      }
    }));

    setReactFlowNodes([...noteNodes, ...clusterNodes]);
  }, [filteredNotes, clusters, analysisSession, setReactFlowNodes, handleMoveNoteToCluster, handleUpdateCluster, handleDeleteCluster, handleRemoveNoteFromCluster]);

  // Early returns for auth
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg">Authenticating...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (!analysisId) {
    navigate('/');
    return null;
  }

  const handleNoteTypeToggle = (noteType: NoteType) => {
    const newSelected = new Set(selectedNoteTypes);
    if (newSelected.has(noteType)) {
      newSelected.delete(noteType);
    } else {
      newSelected.add(noteType);
    }
    setSelectedNoteTypes(newSelected);
  };

  const handleCreateCluster = async () => {
    if (!newClusterName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for the cluster.",
        variant: "destructive",
      });
      return;
    }

    try {
      const cluster = await analysisClusterService.create(analysisId, {
        name: newClusterName.trim(),
        description: newClusterDescription.trim() || undefined,
        position_x: 500,
        position_y: 300,
      });

      setClusters(prev => [...prev, cluster]);
      setNewClusterName('');
      setNewClusterDescription('');
      setIsCreateClusterOpen(false);

      toast({
        title: "Cluster Created",
        description: "Your new theme cluster has been created.",
      });
    } catch (error) {
      console.error('Error creating cluster:', error);
      toast({
        title: "Error",
        description: "Failed to create cluster. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRunAnalysis = async () => {
    if (allNotes.length === 0) {
      toast({
        title: "No Notes to Analyze",
        description: "Add some notes to your interviews before running analysis.",
        variant: "destructive",
      });
      return;
    }

    // Check usage limits before running analysis
    const { canRun, currentUsage, limit } = await checkCanRunAnalysis();
    if (!canRun) {
      toast({
        title: "Monthly Analysis Limit Reached",
        description: `You've used ${currentUsage}/${limit} analyses this month. Limit resets next month.`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAnalyzing(true);
      
      // Prepare notes for analysis with interview context
      const notesForAnalysis = allNotes.map(note => ({
        type: note.type,
        content: note.content,
        created_at: note.created_at,
        interview_name: note.source_interview_name,
        interview_id: note.source_interview_id
      }));

      // Include interview-specific questions if available
      const interviewNames = [...new Set(allNotes.map(note => note.source_interview_name))];
      const questions = [`Analysis across ${interviewNames.length} interviews: ${interviewNames.join(', ')}`];

      const results = await analyzeNotes(notesForAnalysis, questions, analysisSession?.note_space_ids);
      setAnalysisResults(results);
      setIsAnalysisPanelOpen(true);

      toast({
        title: "Analysis Complete",
        description: `Successfully analyzed ${allNotes.length} notes from ${interviewNames.length} interviews.`,
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Failed to analyze notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg">Loading analysis...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">Error loading analysis</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const interviewStats = analysisSession?.note_space_ids.reduce((acc, spaceId) => {
    const spaceNotes = allNotes.filter(note => note.source_interview_id === spaceId);
    const spaceName = spaceNotes[0]?.source_interview_name || `Interview ${spaceId.slice(0, 8)}`;
    acc[spaceName] = spaceNotes.length;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Navbar
        sessionName={analysisSession?.name || 'Multi-Interview Analysis'}
        onSessionNameChange={(name) => {
          if (analysisSession) {
            analysisSessionService.update(analysisSession.id, { name });
            setAnalysisSession({ ...analysisSession, name });
          }
        }}
        showSharing={false}
      />
      
      <div className="flex flex-grow overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <h2 className="text-lg font-semibold mb-2">Multi-Interview Analysis</h2>
            <p className="text-sm text-gray-600 mb-4">
              Discover patterns across {analysisSession?.note_space_ids.length || 0} interviews
            </p>

            {/* Interview Summary */}
            <Card className="p-3 mb-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Interviews ({Object.keys(interviewStats).length})
              </h3>
              <div className="space-y-1 text-sm">
                {Object.entries(interviewStats).map(([name, count]) => (
                  <div key={name} className="flex justify-between">
                    <span className="truncate">{name}</span>
                    <Badge variant="outline" className="text-xs">{count}</Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Search */}
            <div className="mb-4">
              <Label className="text-sm font-medium mb-2 block">Search Notes</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search content or interview name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {/* Filter by Note Type */}
            <div className="mb-6">
              <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter by Type ({selectedNoteTypes.size}/{noteTypes.length})
              </Label>
              <div className="space-y-2">
                {noteTypes.map(({ type, label, color }) => {
                  const count = allNotes.filter(note => note.type === type).length;
                  return (
                    <div
                      key={type}
                      className={`p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedNoteTypes.has(type) ? color : 'bg-gray-50 text-gray-500'
                      }`}
                      onClick={() => handleNoteTypeToggle(type)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">{label}</span>
                        <Badge variant="secondary" className="text-xs">{count}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator className="my-4" />

            {/* AI Analysis */}
            <div className="mb-6">
              <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                AI Cross-Interview Analysis
              </Label>
              <Button
                onClick={handleRunAnalysis}
                disabled={isAnalyzing || allNotes.length === 0}
                className="w-full"
                variant="outline"
              >
                {isAnalyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze All Interviews
                  </>
                )}
              </Button>
              {analysisResults && (
                <Button
                  onClick={() => setIsAnalysisPanelOpen(true)}
                  variant="secondary"
                  size="sm"
                  className="w-full mt-2"
                >
                  View Analysis Results
                </Button>
              )}
            </div>

            <Separator className="my-4" />

            {/* Clusters */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Theme Clusters ({clusters.length})
                </Label>
                <Dialog open={isCreateClusterOpen} onOpenChange={setIsCreateClusterOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Theme Cluster</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label htmlFor="cluster-name">Theme Name</Label>
                        <Input
                          id="cluster-name"
                          value={newClusterName}
                          onChange={(e) => setNewClusterName(e.target.value)}
                          placeholder="e.g., User Frustrations, Feature Requests"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cluster-description">Description (Optional)</Label>
                        <Textarea
                          id="cluster-description"
                          value={newClusterDescription}
                          onChange={(e) => setNewClusterDescription(e.target.value)}
                          placeholder="Describe this theme or pattern..."
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button onClick={handleCreateCluster} className="flex-1">
                          <Target className="w-4 h-4 mr-2" />
                          Create Cluster
                        </Button>
                        <Button variant="outline" onClick={() => setIsCreateClusterOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              {clusters.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No theme clusters yet.</p>
                  <p className="text-xs">Create clusters to group related insights.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {clusters.map(cluster => (
                    <Card key={cluster.id} className="p-3">
                      <div className="font-medium text-sm truncate">{cluster.name}</div>
                      {cluster.description && (
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {cluster.description}
                        </div>
                      )}
                      <Badge variant="outline" className="text-xs mt-2">
                        {cluster.note_ids.length} notes
                      </Badge>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <ReactFlowProvider>
            <ReactFlow
              nodes={reactFlowNodes}
              edges={reactFlowEdges}
              onNodesChange={handleNodesChange}
              onEdgesChange={onEdgesChange}
              onInit={(instance: ReactFlowInstance) => setReactFlowInstance(instance)}
              nodeTypes={nodeTypes}
              fitView
              className="bg-gray-50"
              nodesDraggable={true}
              nodesConnectable={false}
              elementsSelectable={true}
              panOnScroll={false}
              zoomOnScroll={true}
              panOnDrag={true}
              minZoom={0.1}
              maxZoom={2}
            >
              <MiniMap />
              <Controls showZoom={true} showFitView={true} showInteractive={true} />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      </div>

      {/* Analysis Panels */}
      {isMobile ? (
        <MobileAnalysisPanel
          isOpen={isAnalysisPanelOpen}
          onClose={() => setIsAnalysisPanelOpen(false)}
          isAnalyzing={isAnalyzing}
          results={analysisResults}
          isMultiInterview={true}
          interviewNames={Object.keys(interviewStats)}
        />
      ) : (
        <AnalysisPanel
          isOpen={isAnalysisPanelOpen}
          onClose={() => setIsAnalysisPanelOpen(false)}
          isAnalyzing={isAnalyzing}
          results={analysisResults}
          isMultiInterview={true}
          interviewNames={Object.keys(interviewStats)}
        />
      )}
    </div>
  );
};

export default MultiAnalysis;